import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { makeGet, makePost } from "@culturehq/client";
import { Button, Icon, ModalDialog, PlainButton, SelectField, Thumbnail, Warning } from "@culturehq/components";

import { useProfile } from "app/context";
import { Trans } from "app/utils";
import { MAX_TIKTOK_VIDEO_DURATION } from "app/utils/constants";
import { tiktokAuthenticated } from "components/profile/ProfileSocial";
import ChqModalDialog from "components/common/ChqModalDialog";
import getUserName from "app/utils/getUserName";
import SocialMediaPreviewCard from "./stories/SocialMediaPreviewCard";

const TiktokShare = ({
  approvedMessage, children, story, onClose: onCloseMenu
}) => {
  const { profile } = useProfile();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [shareMessage, setShareMessage] = useState();
  const [tiktokAccounts, setTiktokAccounts] = useState([]);
  const [tiktokAuthor, setTiktokAuthor] = useState();
  const isShareable = story.media.duration >= 3
    && story.media.duration <= MAX_TIKTOK_VIDEO_DURATION;

  useEffect(
    () => {
      const approvedForSocialMessage = approvedMessage?.socialType === "tiktok";
      if (approvedForSocialMessage?.message?.length > 0) {
        setShareMessage(approvedForSocialMessage.message);
      } else {
        setShareMessage(story.body ? `${JSON.parse(story.body).blocks.map(block => block.text).join("\n")}` : "");
      }
    }, [approvedMessage, story, profile]
  );

  useEffect(
    () => {
      if (open) {
        if (!tiktokAuthenticated(profile)) {
          const tiktokSession = async () => {
            const { url } = await makeGet("/tiktok/integration", { url: location.pathname });
            window.location.href = url;
          };

          tiktokSession();
        } else {
          const accounts = profile.userTiktoks.filter(usertiktok => !!usertiktok.username).map(
            usertiktok => ({ label: usertiktok.username, value: usertiktok.username })
          );
          setTiktokAccounts(accounts);
          setTiktokAuthor(accounts[0].value);
        }
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, profile]
  );

  const onOpen = () => {
    setOpen(true);
  };

  const onClose = useCallback(
    () => {
      setOpen(false);
      if (onCloseMenu) {
        onCloseMenu();
      }
    },
    [onCloseMenu, setOpen]
  );

  const onAuthorChanged = value => {
    setTiktokAuthor(value);
  };

  const onShare = async () => {
    if (isShareable) {
      await makePost("/tiktok/posts", { storyId: story.id, username: tiktokAuthor });
      onClose();
    }
  };

  return (
    <>
      {children && (
        <div onClick={onOpen} role="button" tabIndex={0}>
          {children}
        </div>
      )}
      {!children && (
        <PlainButton onClick={onOpen}>
          <Icon icon="code" /><Trans>Share on Tiktok</Trans>
        </PlainButton>
      )}
      {open && (
        <ChqModalDialog onClose={onClose} className="TiktokShareModal">
          <ModalDialog.Heading onClose={onClose}>
            <Trans>Share to Tiktok</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            <div className="TiktokShareModal__Accounts">
              <Thumbnail className="Thumbnail" image={profile.avatar.thumbUrl} title={getUserName(profile)} />
              <SelectField
                value={tiktokAuthor}
                onChange={onAuthorChanged}
                options={tiktokAccounts}
                required
              />
            </div>
            {!isShareable && (
              <Warning>
                <Trans>
                  Tiktok only allows videos with at least 3 seconds and at most 60 seconds.
                  You have to trim the video if you want to share it to TikTok
                </Trans>
              </Warning>
            )}
            <SocialMediaPreviewCard
              category="tiktok"
              profile={profile}
              shareMessage={shareMessage}
              story={story}
              username={tiktokAuthor}
            />
            <div className="ButtonContainer">
              <Button disabled={!isShareable} onClick={onShare}>
                <Trans>Send to TikTok</Trans>
              </Button>
            </div>
          </ModalDialog.Body>
        </ChqModalDialog>
      )}
    </>
  );
};

export default TiktokShare;
